import { MIDDLE_C } from '@arpeggi/shared'
import Kali from '../lib/kali/Kali'
import { SAMPLING_RATE } from './AudioConstants'
import { calculatePlaybackRate, ticksToSeconds } from './ElementaryUtils'

/**
 * Returns the VFS path string for a given clip object
 * @param clip an IClipStore object -- this CANNOT be imported because we used this in the offline renderer
 * @param audioBuffer the audioBuffer for the clip's sample
 * @param tempo the tempo of the current song
 */
export const getClipVFSPath = (clip: any, audioDuration: number, tempo: number): string => {
  let start: number = clip.startInComposition48n
  const end: number = clip.endInComposition48n
  const transpose: number = clip.sampleWithEffects.transpose
  const warp: number = clip.sampleWithEffects.warp
  const reverse: boolean = clip.sampleWithEffects.reverse

  const sampleLengthSeconds = audioDuration
  const offsetSeconds = ticksToSeconds(clip.offset48n, tempo)
  const offsetPercent = offsetSeconds / (sampleLengthSeconds * warp)
  const endPercent =
    ticksToSeconds(end - (start - clip.offset48n), tempo) / (sampleLengthSeconds * warp)

  const path = `sample:${clip.sampleWithEffects.sampleIndex}:startoffset:${offsetPercent.toFixed(
    3
  )}:endoffset:${endPercent.toFixed(3)}:warp:${warp.toFixed(
    3
  )}:transpose:${transpose}:reverse:${reverse}`

  return path
}

/**
 * @param float32Arr an array of Float32Arrays containing the audio data for each channel
 * @returns an AudioBuffer object created from the Float32Array array
 */
export function float32ArrayToAudioBuffer(float32Arr: Float32Array[]): AudioBuffer | null {
  if (float32Arr.length === 0) return null
  const audioBuffer = new AudioBuffer({
    length: float32Arr[0].length,
    numberOfChannels: float32Arr.length,
    sampleRate: SAMPLING_RATE
  })
  for (let channelI = 0; channelI < audioBuffer.numberOfChannels; ++channelI) {
    const samples = float32Arr[channelI]
    audioBuffer.copyToChannel(samples, channelI)
  }
  return audioBuffer
}

/**
 * A portable function that creates the audio buffer for a clip to be stored in the virtual file
 * system, as storing in the VFS is much more efficient than calculating in real time and storing
 * in a cache.
 * @param clip an IClipStore object -- this CANNOT be imported because we used this in the offline renderer
 * @param audioBuffer the audioBuffer for the clip's sample
 * @param tempo the tempo of the current song
 */
export const generateClipAudioBuffer = (
  clip: any,
  audioBuffer: any, // have to type this as any since web workers don't support AudioBuffer
  tempo: number,
  audioDuration: number | null = null // only needed when the 'audioBuffer' input is a Float32Array[]
): { path: string; channelData: Float32Array[] } => {
  if (!audioBuffer) {
    console.log('no audio buffer found')
    return { path: '', channelData: [] }
  }

  const audioIsFloat32Arr = Array.isArray(audioBuffer)

  const start: number = clip.startInComposition48n
  const end: number = clip.endInComposition48n
  const transpose: number = clip.sampleWithEffects.transpose
  const warp: number = clip.sampleWithEffects.warp
  const reverse: boolean = clip.sampleWithEffects.reverse

  const playbackRate = calculatePlaybackRate(MIDDLE_C + transpose)
  const stretchFactor = 1.0 / playbackRate

  const sampleLengthSeconds = audioIsFloat32Arr ? audioDuration : audioBuffer.duration
  const offsetSeconds = ticksToSeconds(clip.offset48n, tempo)
  const offsetPercent = offsetSeconds / (sampleLengthSeconds * warp)
  const endPercent =
    ticksToSeconds(end - (start - clip.offset48n), tempo) / (sampleLengthSeconds * warp)

  const path = getClipVFSPath(clip, sampleLengthSeconds, tempo)

  const channelData: Float32Array[] = []
  for (let channel = 0; channel < 2; channel++) {
    let channelAudioFloatArray = audioIsFloat32Arr
      ? audioBuffer[channel]
      : new Float32Array(
          audioBuffer.getChannelData(channel >= audioBuffer.numberOfChannels ? 0 : channel)
        )
    if (reverse) {
      channelAudioFloatArray = channelAudioFloatArray.slice().reverse() // we have to call .slice() to create a copy of the array as .reverse() is done in place
    }
    let croppedChanelAudio: Float32Array = channelAudioFloatArray.subarray(
      Math.floor(offsetPercent * channelAudioFloatArray.length),
      Math.floor(endPercent * channelAudioFloatArray.length)
    )

    /**
     * Transposing the clip:
     * At the time of writing this code, elementary has no way to pitch shift without
     * changing the length of the sample -- we use Kali to change the length of the sample
     * to counteract the length change from elementary (as Kali doesn't change pitch when
     * changing the sample length) -- pretty hacky solution but it works
     * Workflow is as follows: Stretch the clip a certain length, offset that stretch with a different
     * playback rate from elementary. Elementary is performing the "transpose" part, Kali is performing
     * the stretching part (for both transpose and warp).
     */
    let data: Float32Array
    if (transpose !== 0 || warp !== 1) {
      let kali = new Kali(1)
      // if the mode is offline, we watch to perform a high quality transpose, otherwise we want quick
      kali.setup(SAMPLING_RATE, stretchFactor / warp, true) // mode !== 'offline')
      kali.input(croppedChanelAudio)
      kali.process()
      // need to prepare a float array to load the output data into
      data = new Float32Array(croppedChanelAudio.length * playbackRate * warp)
      kali.output(data)
      kali.flush()
    } else {
      data = croppedChanelAudio
    }
    channelData.push(data)
  }

  return { path, channelData }
}
